// src/app/companies/company-single/company-single.component.ts
import { Component, computed, inject, signal, OnInit, DestroyRef, afterNextRender } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { SeoService } from '@/app/core/seo/seo.service';
import { htmlToText } from '@/app/core/seo/seo.utils';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, of, switchMap } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { JobSearchCardComponent } from '../../jobs/job-search-card/job-search-card.component';
import { YoutubeEmbedPipe } from '@/app/pipes/youtube-embed.pipe';
import { JobPositionPublicDto, MatchedLeadsProductPublicDto } from '@/app/models/public';
import { MatDialog } from '@angular/material/dialog';
import { DriverApplicationModalComponent } from '../../applications/driver-application-modal/driver-application-modal.component';

interface SocialLink {
  network: string;
  label: string;
  url: string;
  icon: string;
}

@Component({
  selector: 'app-company-single',
  templateUrl: './company-single.component.html',
  imports: [
    RouterLink,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    JobSearchCardComponent,
    YoutubeEmbedPipe,
  ],
})
export class CompanySingleComponent implements OnInit {

  private http = inject(HttpClient);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private seo = inject(SeoService);
  private destroyRef = inject(DestroyRef);
  private dialog = inject(MatDialog);

  company = signal<MatchedLeadsProductPublicDto | null>(null);
  jobs = signal<JobPositionPublicDto[]>([]);
  loading = signal(true);
  jobsLoading = signal(false);
  notFound = signal(false);

  descriptionExpanded = signal(false);

  logo = computed(() => this.company()?.company?.imageUrl ?? null);
  phone = computed(() => this.company()?.company?.phone ?? null);
  phoneHref = computed(() => this.phone()?.replace(/[^+\d]/g, '') ?? null);

  description = computed(() =>
    this.company()?.profileDetails ?? this.company()?.biography ?? ''
  );

  descriptionPlainText = computed(() =>
    this.description().replace(/<[^>]*>/g, '').trim()
  );

  descriptionSnippet = computed(() => {
    const words = this.description().split(/\s+/).filter(Boolean);
    return words.length > 75
      ? words.slice(0, 75).join(' ') + '...'
      : words.join(' ');
  });

  descriptionDisplay = computed(() =>
    this.descriptionExpanded() ? this.description() : this.descriptionSnippet()
  );

  descriptionCanToggle = computed(() =>
    this.descriptionPlainText().split(/\s+/).filter(Boolean).length > 75
  );

  toggleDescription(): void {
    this.descriptionExpanded.update(v => !v);
  }

  socialLinks = computed<SocialLink[]>(() => {
    const c = this.company();
    if (!c) return [];

    const links: SocialLink[] = [];

    if (c.defaultSocial) //Eventually replace with website link
      // links.push({ network: 'website',   label: 'Website',   url: c., icon: 'https://img.icons8.com/ios/30/domain--v1.png' });
      if (c.facebookHandle)
        links.push({ network: 'facebook', label: 'Facebook', url: c.facebookHandle, icon: 'https://img.icons8.com/ios-glyphs/50/facebook-new.png' });
    if (c.twitterHandle)
      links.push({ network: 'x', label: 'X', url: c.twitterHandle, icon: 'https://img.icons8.com/ios-filled/50/twitterx--v1.png' });
    if (c.instagramHandle)
      links.push({ network: 'instagram', label: 'Instagram', url: c.instagramHandle, icon: 'https://img.icons8.com/ios-glyphs/50/instagram-new--v1.png' });
    if (c.youtubePlaylistId)
      links.push({ network: 'youtube', label: 'YouTube', url: c.youtubePlaylistId, icon: 'https://img.icons8.com/ios-glyphs/50/youtube-play.png' });
    if (c.tiktokHandle)
      links.push({ network: 'tiktok', label: 'TikTok', url: c.tiktokHandle, icon: 'https://img.icons8.com/ios-filled/50/tiktok--v1.png' });

    return links;
  });

  ngOnInit(): void {
    this.route.paramMap
      .pipe(
        switchMap(params => {
          const slug = params.get('slug') ?? '';
          this.loading.set(true);
          this.notFound.set(false);
          this.jobs.set([]);

          return this.http.get<MatchedLeadsProductPublicDto>(`/api/public/companies/${slug}`).pipe(
            catchError(() => {
              this.notFound.set(true);
              return of(null);
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(company => {
        this.company.set(company);
        this.loading.set(false);

        if (company) {
          const profile = htmlToText(company.profileDetails) || htmlToText(company.biography);
          this.seo.setPage({
            title: `${company.name} Truck Driving Jobs`,
            description: `${company.name} is hiring CDL truck drivers. `
              + (profile || 'View open jobs, pay, and benefits, and apply on Truck Drivers USA.'),
            image: company.heroImage || company.company?.imageUrl,
            imageAlt: company.name,
            path: `/companies/${company.slug}`,
          });
        } else {
          this.seo.setPage({
            title: 'Company Not Found',
            description: 'This company is no longer hiring on Truck Drivers USA. Browse other trucking companies hiring CDL drivers.',
          });
        }
      });
  }

  constructor() {
    // Runs only in the browser after first render — never during SSR
    afterNextRender(() => {
      this.route.paramMap
        .pipe(
          switchMap(params => {
            const slug = params.get('slug') ?? '';
            this.jobsLoading.set(true);

            return this.http.get<JobPositionPublicDto[]>(`/api/public/companies/${slug}/jobs`, {
              params: { limit: '10', offset: '0' },
            }).pipe(
              catchError(() => of([])),
            );
          }),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe(jobs => {
          this.jobs.set(jobs);
          this.jobsLoading.set(false);
        });
    });
  }

  scrollToJobs(): void {
    document.getElementById('open-jobs')?.scrollIntoView({ behavior: 'smooth' });
  }

  openApplicationModal(): void {
    this.dialog.open(DriverApplicationModalComponent, {
      width: '100%',
      maxWidth: '1024px',
      height: '100dvh',
      panelClass: 'application-modal',
      // Pass data via inputs using the new signal-based input binding
      data: {
        matchedLeadsProduct: this.company(),
      },
    });
  }
}
